import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import SuggestedArticleCard from './SuggestedArticleCard';
import { IconButton } from 'Components/Button';
import { ICONS } from 'Utils/icons';
import { Button } from 'react-bootstrap';

const OVERFLOW_EPSILON_PX = 1;
const POSITION_EPSILON_PX = 6;
const SCROLL_SETTLE_DELAY_MS = 150;

const ArticleCarousel = ({ heading, articles, id }) => {
  const trackRef = useRef(null);
  const cardRefs = useRef([]);
  const cardFocusRefs = useRef([]);
  const tickingRef = useRef(false);
  const rafIdRef = useRef(null);
  const pendingTargetRef = useRef(null);
  const settleTimeoutRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const resolveBoundaryIndex = () => {
    const track = trackRef.current;
    if (!track) return null;
    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    if (track.scrollLeft <= POSITION_EPSILON_PX) return 0;
    if (maxScrollLeft - track.scrollLeft <= POSITION_EPSILON_PX) return articles.length - 1;
    return null;
  };

  const updateActiveFromScroll = () => {
    const track = trackRef.current;
    if (!track) return;

    const boundaryIndex = resolveBoundaryIndex();
    if (boundaryIndex != null) {
      setActiveIndex(boundaryIndex);
      return;
    }

    const trackCenter = track.getBoundingClientRect().left + track.clientWidth / 2;
    let closestIndex = 0;
    let closestDist = Infinity;
    cardRefs.current.forEach((el, i) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      const dist = Math.abs(r.left + r.width / 2 - trackCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    });
    setActiveIndex(closestIndex);
  };

  const armSettleTimer = () => {
    if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
    settleTimeoutRef.current = setTimeout(() => {
      pendingTargetRef.current = null;
    }, SCROLL_SETTLE_DELAY_MS);
  };

  const cancelPendingTarget = () => {
    pendingTargetRef.current = null;
  };

  const handleScroll = () => {
    if (pendingTargetRef.current != null) {
      armSettleTimer();
      return;
    }
    if (tickingRef.current) return;
    tickingRef.current = true;
    rafIdRef.current = requestAnimationFrame(() => {
      updateActiveFromScroll();
      tickingRef.current = false;
    });
  };

  useEffect(() => () => {
    if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
    if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
  }, []);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const checkOverflow = () => {
      setIsOverflowing(track.scrollWidth - track.clientWidth > OVERFLOW_EPSILON_PX);
    };
    checkOverflow();
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(track);
    return () => ro.disconnect();
  }, [articles.length]);

  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(articles.length - 1, 0)));
  }, [articles.length]);

  const getVisibleCardCount = () => {
    const track = trackRef.current;
    const first = cardRefs.current[0];
    if (!track || !first) return 1;
    const cardWidth = first.getBoundingClientRect().width;
    const second = cardRefs.current[1];
    const step = second ? second.getBoundingClientRect().left - first.getBoundingClientRect().left : cardWidth;
    return step > 0 ? Math.max(1, Math.round(track.clientWidth / step)) : 1;
  };

  const getWindowStart = (i) => {
    const visibleCount = getVisibleCardCount();
    const centerSlot = Math.floor((visibleCount - 1) / 2);
    const maxWindowStart = Math.max(articles.length - visibleCount, 0);
    return Math.min(Math.max(i - centerSlot, 0), maxWindowStart);
  };

  const isCardFullyVisible = (i) => {
    const track = trackRef.current;
    const el = cardRefs.current[i];
    if (!track || !el) return false;
    const trackRect = track.getBoundingClientRect();
    const style = getComputedStyle(track);
    const contentLeft = trackRect.left + (parseFloat(style.paddingLeft) || 0);
    const contentRight = trackRect.right - (parseFloat(style.paddingRight) || 0);
    const cardRect = el.getBoundingClientRect();
    return cardRect.left >= contentLeft - POSITION_EPSILON_PX
        && cardRect.right <= contentRight + POSITION_EPSILON_PX;
  };

  const resolveTargetIndex = (i, exact) => {
    if (exact) return i;
    const windowStart = getWindowStart(i);
    const maxWindowStart = Math.max(articles.length - getVisibleCardCount(), 0);
    if (windowStart === 0) return 0;
    if (windowStart === maxWindowStart) return articles.length - 1;
    return i;
  };

  const goToIndex = (i, { exact = false } = {}) => {
    if (isCardFullyVisible(i)) {
      setActiveIndex(i);
      return;
    }
    const target = resolveTargetIndex(i, exact);
    const windowStart = getWindowStart(i);
    const el = cardRefs.current[windowStart];
    if (!el) return;
    pendingTargetRef.current = target;
    armSettleTimer();
    setActiveIndex(target);
    el.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  };

  const goPrev = () => goToIndex(Math.max(activeIndex - getVisibleCardCount(), 0));
  const goNext = () => goToIndex(Math.min(activeIndex + getVisibleCardCount(), articles.length - 1));

  const focusCard = (i) => {
    cardFocusRefs.current[i]?.focus({ preventScroll: true });
  };

  const getDotLabel = (i) => `Go to article ${i + 1} of ${articles.length}`;

  const headingId = `${id}-heading`;
  const prevDisabled = activeIndex <= 0;
  const nextDisabled = activeIndex >= articles.length - 1;

  return (
    <div className="article-carousel-group mb-5">
      {heading && (
        <h3 id={headingId} className="header1 bold color-primary-navyblue mb-4">{heading}</h3>
      )}

      <div
        role="region"
        aria-roledescription="carousel"
        aria-labelledby={heading ? headingId : undefined}
        aria-label={!heading ? 'Suggested articles' : undefined}
        aria-live="polite"
      >
        <ul
          className="article-carousel-track"
          ref={trackRef}
          onScroll={handleScroll}
          onPointerDown={cancelPendingTarget}
          onWheel={cancelPendingTarget}
        >
          {articles.map((art, i) => (
            <li
              key={`${art.docId ?? art.groupId}-${i}`}
              ref={(el) => { cardRefs.current[i] = el; }}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${articles.length}`}
              className="article-carousel-slide"
            >
              <SuggestedArticleCard
                article={art}
                ref={(el) => { cardFocusRefs.current[i] = el; }}
              />
            </li>
          ))}
        </ul>

        <div className="article-carousel-controls justify-content-center justify-content-md-between">
          <IconButton
            id={`${id}-prev`}
            className={`ms-2 article-carousel-btn d-none d-md-flex${isOverflowing ? '' : ' is-hidden'}${prevDisabled ? ' icon-button-aria-disabled' : ''}`}
            icon={ICONS.articleArrowLeft}
            hiddenText="Previous slide"
            onClick={goPrev}
            aria-disabled={prevDisabled}
            aria-describedby={prevDisabled ? `${id}-prev-status` : undefined}
            variant="comp-clear"
          />

          <div className="article-carousel-dots" role="group" aria-label="Slide controls">
            {articles.map((_, i) => (
                <Button
                    key={i}
                    aria-label={getDotLabel(i)}
                    variant="carousel-dot"
                    aria-disabled={i === activeIndex ? true : false}
                    className={`article-carousel-dot${i === activeIndex ? ' active' : ''}${isOverflowing ? '' : ' is-hidden'}`}
                    onClick={()=>{
                        goToIndex(i, { exact: true });
                        focusCard(i);
                    }}
                >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="6" cy="6" r="6" fill="#071E31"/>
                    </svg>
                </Button>

            ))}
          </div>

          <IconButton
            id={`${id}-next`}
            className={`me-2 article-carousel-btn d-none d-md-flex${isOverflowing ? '' : ' is-hidden'}${nextDisabled ? ' icon-button-aria-disabled' : ''}`}
            icon={ICONS.articleArrowRight}
            hiddenText="Next slide"
            onClick={goNext}
            aria-disabled={nextDisabled}
            aria-describedby={nextDisabled ? `${id}-next-status` : undefined}
            variant="comp-clear"
          />

          <span id={`${id}-prev-status`} className="visually-hidden">Beginning of list reached. Previous slide button is inactive.</span>
          <span id={`${id}-next-status`} className="visually-hidden">End of list reached. Next slide button is inactive.</span>
        </div>
      </div>
    </div>
  );
};

export default ArticleCarousel;
